import {
  Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges,
  AfterViewInit, ViewEncapsulation, ChangeDetectorRef
} from '@angular/core';
import { Subscription, interval, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import * as L from 'leaflet';

import { CommandeService } from 'src/app/core/services/commande.service';
import { SseNotificationService } from 'src/app/core/services/sse-notification.service';

@Component({
  selector: 'app-tracking-map',
  templateUrl: './tracking-map.component.html',
  styleUrls: ['./tracking-map.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TrackingMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @Input() commandeId!: number;
  @Input() departLat?: number;
  @Input() departLng?: number;
  @Input() arriveeLat?: number;
  @Input() arriveeLng?: number;
  @Input() statut?: string;
  @Input() departTexte?: string;
  @Input() arriveeTexte?: string;

  map: L.Map | null = null;
  private markerLivreur: L.Marker | null = null;
  private markerDepart: L.Marker | null = null;
  private markerArrivee: L.Marker | null = null;
  private routeLayer: L.Polyline | null = null;

  positionLivreur: { latitude: number; longitude: number } | null = null;
  derniereMiseAJour: Date | null = null;
  positionIndisponible = false;
  mapInitialisee = false;

  private sseSub?: Subscription;
  private pollSub?: Subscription;
  private readonly POLL_INTERVAL_MS = 20000;

  private readonly STATUTS_ACTIFS = ['AFFECTE', 'EN_PREPARATION', 'EN_LIVRAISON'];

  constructor(
    private commandeService: CommandeService,
    private sse: SseNotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  get livreurEnMouvement(): boolean {
    return this.STATUTS_ACTIFS.includes(this.statut ?? '');
  }

  get labelDestination(): string {
    if (this.statut === 'EN_LIVRAISON') return 'Lieu de livraison';
    return 'Lieu de récupération';
  }

  ngOnInit(): void {
    this.configureLeafletIcons();
    this.chargerPositionInitiale();
    this.demarrerTracking();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initMap();
      // Recalcule les dimensions après que tout parent animé a fini sa transition
      setTimeout(() => { if (this.map) this.map.invalidateSize(); }, 500);
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['statut'] && !changes['statut'].firstChange) {
      if (this.livreurEnMouvement) {
        this.demarrerTracking();
      } else {
        this.arreterTracking();
      }
    }
    if ((changes['departLat'] || changes['departLng'] || changes['arriveeLat'] || changes['arriveeLng']) && this.mapInitialisee) {
      this.poserMarqueursParcours();
    }
  }

  ngOnDestroy(): void {
    this.arreterTracking();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private configureLeafletIcons(): void {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
    });
  }

  private initMap(): void {
    const el = document.getElementById(`tracking-map-${this.commandeId}`);
    if (!el || this.map) return;

    const centerLat = this.departLat || 5.3364;
    const centerLng = this.departLng || -4.0267;

    this.map = L.map(`tracking-map-${this.commandeId}`, { zoomControl: true, scrollWheelZoom: false })
      .setView([centerLat, centerLng], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19
    }).addTo(this.map);

    this.mapInitialisee = true;
    this.poserMarqueursParcours();

    if (this.positionLivreur) {
      this.mettreAJourMarqueurLivreur(this.positionLivreur.latitude, this.positionLivreur.longitude);
    }
  }

  private async geocoderAdresse(adresse: string): Promise<{ lat: number; lng: number } | null> {
    if (!adresse?.trim()) return null;
    try {
      const q = encodeURIComponent(adresse + ', Côte d\'Ivoire');
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
        headers: { 'Accept-Language': 'fr' }
      });
      const data = await r.json();
      if (data?.length) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch { /* ignore */ }
    return null;
  }

  private async poserMarqueursParcours(): Promise<void> {
    if (!this.map) return;

    let dLat = this.departLat, dLng = this.departLng;
    let aLat = this.arriveeLat, aLng = this.arriveeLng;

    // Géocodage de secours si coordonnées absentes
    if ((!dLat || !dLng) && this.departTexte) {
      const geo = await this.geocoderAdresse(this.departTexte);
      if (geo) { dLat = geo.lat; dLng = geo.lng; }
    }
    if ((!aLat || !aLng) && this.arriveeTexte) {
      const geo = await this.geocoderAdresse(this.arriveeTexte);
      if (geo) { aLat = geo.lat; aLng = geo.lng; }
    }

    // Marqueur départ (vert) — SVG pin sans rotation CSS
    if (dLat && dLng) {
      const iconDepart = L.divIcon({
        className: '',
        html: `<svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <filter id="sd"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/></filter>
          <path d="M16 40C4 28 2 22 2 14A14 14 0 0 1 30 14C30 22 28 28 16 40Z" fill="#16a34a" stroke="white" stroke-width="2.5" filter="url(#sd)"/>
          <circle cx="16" cy="14" r="7" fill="rgba(255,255,255,0.22)"/>
          <text x="16" y="18" text-anchor="middle" dominant-baseline="middle" font-size="13" font-weight="900" fill="white" font-family="system-ui,Arial,sans-serif" letter-spacing="-0.5">D</text>
        </svg>`,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -44]
      });
      if (this.markerDepart) this.map.removeLayer(this.markerDepart);
      this.markerDepart = L.marker([dLat, dLng], { icon: iconDepart })
        .addTo(this.map)
        .bindPopup(`<b>Récupération</b><br>${this.departTexte || ''}`);
    }

    // Marqueur arrivée (rouge) — SVG pin sans rotation CSS
    if (aLat && aLng) {
      const iconArrivee = L.divIcon({
        className: '',
        html: `<svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <filter id="sa"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/></filter>
          <path d="M16 40C4 28 2 22 2 14A14 14 0 0 1 30 14C30 22 28 28 16 40Z" fill="#dc2626" stroke="white" stroke-width="2.5" filter="url(#sa)"/>
          <circle cx="16" cy="14" r="7" fill="rgba(255,255,255,0.22)"/>
          <text x="16" y="18" text-anchor="middle" dominant-baseline="middle" font-size="13" font-weight="900" fill="white" font-family="system-ui,Arial,sans-serif" letter-spacing="-0.5">A</text>
        </svg>`,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -44]
      });
      if (this.markerArrivee) this.map.removeLayer(this.markerArrivee);
      this.markerArrivee = L.marker([aLat, aLng], { icon: iconArrivee })
        .addTo(this.map)
        .bindPopup(`<b>Livraison</b><br>${this.arriveeTexte || ''}`);
    }

    // Tracer l'itinéraire routier via OSRM
    if (dLat && dLng && aLat && aLng) {
      this.tracerItineraire(dLat, dLng, aLat, aLng);
    }

    // Ajuster le zoom
    const bounds: L.LatLng[] = [];
    if (dLat && dLng) bounds.push(L.latLng(dLat, dLng));
    if (aLat && aLng) bounds.push(L.latLng(aLat, aLng));
    if (bounds.length > 1) {
      this.map.fitBounds(L.latLngBounds(bounds), { padding: [60, 60] });
    } else if (bounds.length === 1) {
      this.map.setView(bounds[0], 14);
    }
    this.cdr.markForCheck();
  }

  private chargerPositionInitiale(): void {
    if (!this.commandeId) return;
    this.commandeService.obtenirPositionLivreur(this.commandeId).subscribe({
      next: (pos) => {
        this.positionIndisponible = false;
        this.positionLivreur = pos;
        this.derniereMiseAJour = new Date();
        if (this.mapInitialisee) {
          this.mettreAJourMarqueurLivreur(pos.latitude, pos.longitude);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.positionIndisponible = true;
        this.cdr.markForCheck();
      }
    });
  }

  private demarrerTracking(): void {
    this.arreterTracking();
    if (!this.livreurEnMouvement) return;

    // SSE en temps réel
    this.sseSub = this.sse.positionLivreur$.subscribe((evt) => {
      if (evt.commandeId !== this.commandeId) return;
      this.positionIndisponible = false;
      this.positionLivreur = { latitude: evt.latitude, longitude: evt.longitude };
      this.derniereMiseAJour = new Date();
      this.mettreAJourMarqueurLivreur(evt.latitude, evt.longitude);
      this.cdr.markForCheck();
    });

    // Polling de secours toutes les 20 s si le livreur ne push pas
    this.pollSub = interval(this.POLL_INTERVAL_MS).pipe(
      switchMap(() => this.commandeService.obtenirPositionLivreur(this.commandeId).pipe(
        catchError(() => of(null))
      ))
    ).subscribe((pos) => {
      if (!pos) { this.positionIndisponible = true; this.cdr.markForCheck(); return; }
      this.positionIndisponible = false;
      this.positionLivreur = pos;
      this.derniereMiseAJour = new Date();
      this.mettreAJourMarqueurLivreur(pos.latitude, pos.longitude);
      this.cdr.markForCheck();
    });
  }

  private arreterTracking(): void {
    this.sseSub?.unsubscribe();
    this.pollSub?.unsubscribe();
  }

  private mettreAJourMarqueurLivreur(lat: number, lng: number): void {
    if (!this.map) return;
    const iconLivreur = L.divIcon({
      className: '',
      html: `<div class="tm-livreur-wrap">
        <div class="tm-livreur__ring tm-livreur__ring--outer"></div>
        <div class="tm-livreur__ring tm-livreur__ring--inner"></div>
        <div class="tm-livreur__core">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="6" cy="17" r="3" stroke="white" stroke-width="2"/>
            <circle cx="18" cy="17" r="3" stroke="white" stroke-width="2"/>
            <path d="M6 17L9 8h6l3 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 8l2-4h4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="17" cy="4" r="1.5" fill="white"/>
          </svg>
        </div>
      </div>`,
      iconSize: [52, 52],
      iconAnchor: [26, 26],
      popupAnchor: [0, -28]
    });

    if (this.markerLivreur) {
      this.markerLivreur.setLatLng([lat, lng]);
    } else {
      this.markerLivreur = L.marker([lat, lng], { icon: iconLivreur })
        .addTo(this.map)
        .bindPopup('<b>Livreur</b> — position en temps réel');
    }
    this.map.panTo([lat, lng], { animate: true, duration: 0.5 });
  }

  private async tracerItineraire(dLat: number, dLng: number, aLat: number, aLng: number): Promise<void> {
    if (!this.map) return;
    if (this.routeLayer) { this.map.removeLayer(this.routeLayer); this.routeLayer = null; }
    try {
      const url = `https://router.project-osrm.org/route/v1/driving/${dLng},${dLat};${aLng},${aLat}?overview=full&geometries=geojson`;
      const resp = await fetch(url);
      const data = await resp.json();
      const coords: [number, number][] = data?.routes?.[0]?.geometry?.coordinates;
      if (coords?.length) {
        const latlngs: L.LatLngExpression[] = coords.map(([lng, lat]) => [lat, lng]);
        // Ombre sous la route
        L.polyline(latlngs, { color: '#1e3a8a', weight: 9, opacity: 0.18, lineCap: 'round', lineJoin: 'round' }).addTo(this.map).bringToBack();
        this.routeLayer = L.polyline(latlngs, {
          color: '#3b82f6',
          weight: 5,
          opacity: 0.9,
          lineCap: 'round',
          lineJoin: 'round',
          className: 'tracking-route-line'
        }).addTo(this.map);
        this.routeLayer.bringToBack();
      }
    } catch { /* ignore routing failure */ }
  }

  ouvrirDansGoogleMaps(): void {
    if (!this.positionLivreur) return;
    const url = `https://www.google.com/maps?q=${this.positionLivreur.latitude},${this.positionLivreur.longitude}`;
    window.open(url, '_blank');
  }
}
